import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  getDetalleCotizacionByIdCotizacion,
  getCotizacionById,
  getAllMonedas,
} from "../../../services/CotizacionService.js";
import { getClienteById } from "../../../services/ClienteService.js";
import { enriquecerSubproductosConProducto } from "../utils/detallePayload.js";

const useDatosCotizacion = (baseUrl, idCliente, idCotizacion) => {
  const location = useLocation();

  const [cotizacion, setCotizacion] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [detallesCobro, setDetallesCobro] = useState([]);
  const [monedaOptions, setMonedaOptions] = useState([]);
  const [monedaOptionsFetched, setMonedaOptionsFetched] = useState(false);
  const [selectedItemsParam, setSelectedItemsParams] = useState(null);

  const fetchMonedaOptions = useCallback(async () => {
    try {
      const data = await getAllMonedas(baseUrl);
      setMonedaOptions(data.monedas);
      setMonedaOptionsFetched(true);
      return data.monedas ?? [];
    } catch (error) {
      console.error("Error fetching data:", error);
      return [];
    }
  }, [baseUrl]);

  const getDetalleData = useCallback(
    async (idCot = idCotizacion) => {
      try {
        // Asegura tener las monedas antes de cargar detalles: la DB guarda
        // tarifa_diaria/tarifa_mensual en CLP para filas CLP y el estado
        // interno trabaja en UF.
        const monedas = await fetchMonedaOptions();
        const valorUf = monedas?.find((m) => m.codigo === "UF")?.tipoCambio || 1;

        const data = await getDetalleCotizacionByIdCotizacion(baseUrl, idCot);

        if (data.mensaje === "OK") {
          const enrichedDetalles = enriquecerSubproductosConProducto(
            data.detalles
          ).map((item) => {
            if (item.moneda !== "CLP") return item;
            const convertir = (valor) =>
              valor == null ||
              valor === "Tarifa Variable" ||
              isNaN(parseFloat(valor))
                ? valor
                : parseFloat(valor) / valorUf;
            return {
              ...item,
              tarifa_diaria: convertir(item.tarifa_diaria),
              tarifa_mensual: convertir(item.tarifa_mensual),
            };
          });
          const encodedParams = enrichedDetalles
            .map((item) => encodeURIComponent(JSON.stringify(item)))
            .join(",");
          setSelectedItemsParams(encodedParams);
          setDetallesCobro(enrichedDetalles);
        } else {
          alert(data.mensaje);
        }
      } catch (error) {
        // Mismo comportamiento silencioso que la implementación original
      }
    },
    [baseUrl, idCotizacion, fetchMonedaOptions]
  );

  const getCotizacionData = useCallback(
    async (idCot = idCotizacion) => {
      try {
        const data = await getCotizacionById(baseUrl, idCot);

        if (data.mensaje === "OK") {
          setCotizacion(data.cotizacion);
        } else {
          alert(data.mensaje);
        }
      } catch (error) {
        // Mismo comportamiento silencioso que la implementación original
      }
    },
    [baseUrl, idCotizacion]
  );

  const fetchDataCliente = useCallback(
    async (idCli = idCliente) => {
      try {
        const data = await getClienteById(baseUrl, idCli);
        setCliente(data);
      } catch (error) {
        console.error("Error fetching client data:", error);
      }
    },
    [baseUrl, idCliente]
  );

  useEffect(() => {
    fetchMonedaOptions();
    getDetalleData();
    getCotizacionData();
    fetchDataCliente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-cargar datos cuando cambien los parámetros de la URL
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const newIdCliente = parseInt(sp.get("idCliente"));
    const newIdCotizacion = parseInt(sp.get("idCotizacion"));

    if (!isNaN(newIdCotizacion)) {
      getDetalleData(newIdCotizacion);
      getCotizacionData(newIdCotizacion);
    }
    if (!isNaN(newIdCliente)) {
      fetchDataCliente(newIdCliente);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return {
    cotizacion,
    cliente,
    detallesCobro,
    setDetallesCobro,
    monedaOptions,
    monedaOptionsFetched,
    selectedItemsParam,
    getDetalleData,
    getCotizacionData,
    fetchDataCliente,
  };
};

export default useDatosCotizacion;
